import {
  addDaysToBaliKey,
  getBaliDateKey,
  toBaliDateKey,
  netMembershipPrice,
  type RefundTotals,
} from '@/lib';
import {
  MEMBERSHIP_COHORTS,
  cohortOfMembership,
  type MembershipCohortKey,
  type MembershipCohortRow,
} from './membershipCohorts';

/**
 * Monthly Recurring Revenue for the Memberships report: every recurring
 * membership's net price normalised to a per-month value (yearly ÷ 12,
 * quarterly ÷ 3, monthly as-is), summed over the memberships whose term covers
 * each Bali calendar day.
 *
 * This exists because headcount overstates stability when the term mix is
 * uneven — 175 monthlies and 175 yearlies are the same line on the "Active
 * Members" chart and wildly different businesses. Breakeven is a rupiah
 * number, so the run-rate is compared against monthly opex directly rather
 * than proxied through a member count.
 *
 * Only the recurring terms are counted. A day pass or a weekly is a purchase,
 * not a run-rate — spreading one over a month would manufacture recurring
 * revenue out of a walk-in. Their money still shows in the revenue charts
 * below; it just isn't MRR.
 */

/** The recurring terms, in stack order (bottom-up), with their divisors. */
export type MrrTermKey = 'monthly' | 'quarterly' | 'yearly';

const cohortColor = (key: MembershipCohortKey): string =>
  MEMBERSHIP_COHORTS.find((c) => c.key === key)?.color ?? '#9CA3AF';

/**
 * Colours come from `MEMBERSHIP_COHORTS`, so each term keeps the identity it
 * already has in the Membership Split pie and the Cohorts stack on this tab.
 * `months` is the nominal term length the price is divided by — nominal on
 * purpose: the sold term defines the run-rate, and the actual date span moves
 * with freezes and admin edits (see `cohortOfMembership`).
 */
export const MRR_TERMS: ReadonlyArray<{
  key: MrrTermKey;
  name: string;
  months: number;
  color: string;
}> = [
  { key: 'monthly', name: 'Monthly', months: 1, color: cohortColor('monthly') },
  { key: 'quarterly', name: 'Quarterly', months: 3, color: cohortColor('quarterly') },
  { key: 'yearly', name: 'Yearly', months: 12, color: cohortColor('yearly') },
];

const TERM_MONTHS: Partial<Record<MembershipCohortKey, number>> = Object.fromEntries(
  MRR_TERMS.map((t) => [t.key, t.months]),
);

/** One membership's contribution to the run-rate, as a covered span. */
export interface MrrSpan {
  /** Inclusive Bali date keys — the days this membership contributes on. */
  start: string;
  end: string;
  term: MrrTermKey;
  /** IDR per month (net price ÷ nominal term months). Float — round at emit. */
  value: number;
}

/**
 * The one place a membership row is qualified for MRR, shared by the MRR
 * chart (`buildMrrSeries`) and the Net MRR Movement waterfall
 * (`buildMrrMovement`). Both charts MUST agree on which rows count and at
 * what per-month value, or the waterfall stops reconciling with the line
 * above it — keeping this in a single helper makes that agreement
 * structural rather than two parallel implementations that happen to match.
 *
 * Returns `null` for rows that carry no run-rate: fully refunded or Rp 0
 * (net ≤ 0), non-recurring terms (daily / weekly / other), and unusable
 * date spans.
 */
export function mrrSpanOfRow(
  row: MembershipCohortRow,
  refundTotals: RefundTotals,
): MrrSpan | null {
  const net = netMembershipPrice(row.id, row.price, refundTotals);
  if (net <= 0) return null;
  const months = TERM_MONTHS[cohortOfMembership(row).key];
  if (!months) return null; // daily / weekly / other — not recurring
  const start = row.start_date ? toBaliDateKey(row.start_date) : '';
  const end = row.end_date ? toBaliDateKey(row.end_date) : '';
  if (!start || !end || end < start) return null;
  const term: MrrTermKey = months === 1 ? 'monthly' : months === 3 ? 'quarterly' : 'yearly';
  return { start, end, term, value: net / months };
}

export interface MrrPoint {
  /** Bali calendar day, YYYY-MM-DD. The x-axis formats this with `dayLabel`. */
  day: string;
  /** IDR per month contributed by each recurring term, rounded to the rupiah. */
  monthly: number;
  quarterly: number;
  yearly: number;
  /** Sum of the three term values above, so the stack always matches. */
  total: number;
}

// Cap on emitted points (~5 years of days), anchored to the NEWEST day — the
// same guard as buildActiveMemberGrowth: one corrupt ancient start_date can
// never truncate current data, it just falls outside the window.
const MAX_DAYS = 1826;

/**
 * MRR per Bali calendar day.
 *
 * A membership contributes on day D when its `start_date … end_date` span
 * covers D (both ends inclusive, Bali date keys — freezes extend `end_date`,
 * so a frozen membership keeps contributing through the extension, which is
 * right: the money is still committed and the term classification comes from
 * the plan name, not the stretched span).
 *
 * What each one contributes is `netMembershipPrice ÷ months`, where months is
 * the NOMINAL term (1 / 3 / 12): net so a fully refunded membership drops out
 * and a partial refund contributes the reduced amount, nominal so the divisor
 * describes what was sold. Prices are gross IDR (PB1 + service inclusive),
 * like every other revenue figure on this page.
 *
 * Memberships are summed independently — a renewal overlapping the old term
 * counts twice for the overlap, because both sold terms are live commitments.
 * (Contrast `buildActiveMemberGrowth`, which merges spans per member: it
 * counts people, this counts money.)
 *
 * `floorDay` pins the left edge exactly as it does for Active Members:
 * coverage that began earlier is clipped into the first point, and days
 * between the floor and the first contribution are emitted at zero.
 */
export function buildMrrSeries(
  rows: MembershipCohortRow[],
  refundTotals: RefundTotals,
  now: Date = new Date(),
  floorDay?: string,
): MrrPoint[] {
  const spans: MrrSpan[] = [];
  for (const row of rows) {
    const span = mrrSpanOfRow(row, refundTotals);
    if (span) spans.push(span);
  }
  if (spans.length === 0) return [];

  const today = getBaliDateKey(now);
  let earliestStart = '';
  for (const span of spans) {
    if (!earliestStart || span.start < earliestStart) earliestStart = span.start;
  }
  // Pre-sales only — nothing has started, no run-rate to draw yet.
  if (earliestStart > today) return [];

  const minStartDay = addDaysToBaliKey(today, -(MAX_DAYS - 1));
  const startDay = floorDay
    ? (floorDay > minStartDay ? floorDay : minStartDay)
    : (earliestStart > minStartDay ? earliestStart : minStartDay);
  if (startDay > today) return [];

  // +value on the first covered day, -value on the day after the last —
  // running the deltas forward gives each day's run-rate in one pass. The
  // subtraction is the exact float added, so the sweep cannot drift.
  const deltas: Record<MrrTermKey, Map<string, number>> = {
    monthly: new Map(),
    quarterly: new Map(),
    yearly: new Map(),
  };
  const bump = (key: MrrTermKey, day: string, by: number) => {
    const map = deltas[key];
    map.set(day, (map.get(day) ?? 0) + by);
  };

  for (const span of spans) {
    if (span.end < startDay || span.start > today) continue;
    bump(span.term, span.start > startDay ? span.start : startDay, span.value);
    if (span.end < today) bump(span.term, addDaysToBaliKey(span.end, 1), -span.value);
  }

  const points: MrrPoint[] = [];
  const running: Record<MrrTermKey, number> = { monthly: 0, quarterly: 0, yearly: 0 };
  for (let day = startDay; day <= today; day = addDaysToBaliKey(day, 1)) {
    for (const term of MRR_TERMS) {
      running[term.key] += deltas[term.key].get(day) ?? 0;
    }
    // Rounded per term at emit, and the total is the sum of the ROUNDED parts,
    // so the tooltip's terms always add up to its total to the rupiah.
    const monthly = Math.round(running.monthly);
    const quarterly = Math.round(running.quarterly);
    const yearly = Math.round(running.yearly);
    points.push({ day, monthly, quarterly, yearly, total: monthly + quarterly + yearly });
  }
  return points;
}
